import { analyzePortfolioImages, getAnalysisStatusMessage, getCostOptimizationMessage } from '@/services/ai/portfolioAnalysis';

export interface PortfolioGenerationRequest {
  imageUrls: string[];
  userProfession?: string;
  userSkills?: string[];
}

export interface PortfolioGenerationResult {
  title: string;
  description: string;
  confidence: number;
  detectedElements: string[];
  analyzedImageCount: number;
  totalImageCount: number;
  statusMessage: string;
  costOptimizationMessage: string;
}

export interface PortfolioGenerationError {
  error: string;
  fallbackResult?: PortfolioGenerationResult;
}

/**
 * Generate portfolio project details using AI analysis
 * Uses intelligent image selection to analyze only 2-3 images for cost efficiency
 * Focuses on analyzing the actual images rather than user skills
 */
export async function generatePortfolioDetails(
  request: PortfolioGenerationRequest
): Promise<PortfolioGenerationResult> {
  try {
    console.log(`[PortfolioGenerator] Starting AI analysis for ${request.imageUrls.length} images`);
    
    // Call the AI analysis service - focus on image analysis, not user skills
    const analysis = await analyzePortfolioImages({
      imageUrls: request.imageUrls,
      userProfession: request.userProfession || 'designer',
      userSkills: [] // Don't pass user skills to focus on image analysis
    });

    // Generate status messages
    const statusMessage = getAnalysisStatusMessage(
      analysis.analyzedImageCount,
      analysis.totalImageCount,
      false
    );
    
    const costOptimizationMessage = getCostOptimizationMessage(request.imageUrls.length);

    console.log(`[PortfolioGenerator] AI analysis completed successfully`);

    return {
      title: analysis.title,
      description: analysis.description,
      confidence: analysis.confidence,
      detectedElements: analysis.detectedElements,
      analyzedImageCount: analysis.analyzedImageCount,
      totalImageCount: analysis.totalImageCount,
      statusMessage,
      costOptimizationMessage
    };

  } catch (error) {
    console.error('[PortfolioGenerator] AI analysis failed:', error);
    
    // Return fallback result with error information
    const fallbackResult: PortfolioGenerationResult = {
      title: "Portfolio Project",
      description: "A creative design project showcasing professional skills and expertise.",
      confidence: 0.5,
      detectedElements: ["design", "portfolio", "project"],
      analyzedImageCount: Math.min(request.imageUrls.length, 3),
      totalImageCount: request.imageUrls.length,
      statusMessage: "AI analysis unavailable - using fallback content",
      costOptimizationMessage: getCostOptimizationMessage(request.imageUrls.length)
    };

    throw {
      error: error instanceof Error ? error.message : 'Unknown error during AI analysis',
      fallbackResult
    };
  }
}

/**
 * Generate portfolio details with fallback handling
 */
export async function generatePortfolioDetailsWithFallback(
  request: PortfolioGenerationRequest
): Promise<PortfolioGenerationResult> {
  try {
    return await generatePortfolioDetails(request);
  } catch (error) {
    console.warn('[PortfolioGenerator] Using fallback due to AI analysis failure:', error);
    
    // Return fallback result if available
    if (error && typeof error === 'object' && 'fallbackResult' in error) {
      return (error as PortfolioGenerationError).fallbackResult!;
    }
    
    // Generate basic fallback
    const totalCount = request.imageUrls.length;
    const analyzedCount = Math.min(totalCount, 3);
    
    return {
      title: "Design Project",
      description: "A professional design project showcasing creative skills and expertise.",
      confidence: 0.3,
      detectedElements: ["design", "portfolio", "project"],
      analyzedImageCount: analyzedCount,
      totalImageCount: totalCount,
      statusMessage: "Using fallback content due to AI analysis failure",
      costOptimizationMessage: getCostOptimizationMessage(totalCount)
    };
  }
}

/**
 * Get analysis status message for UI display
 */
export function getAnalysisStatusMessageForUI(
  analyzedCount: number,
  totalCount: number,
  isAnalyzing: boolean = false
): string {
  return getAnalysisStatusMessage(analyzedCount, totalCount, isAnalyzing);
}

/**
 * Get cost optimization message for UI display
 */
export function getCostOptimizationMessageForUI(totalCount: number): string {
  return getCostOptimizationMessage(totalCount);
}

/**
 * Generate only the portfolio project title using AI analysis
 */
export async function generatePortfolioTitle(
  request: PortfolioGenerationRequest
): Promise<string> {
  try {
    console.log(`[PortfolioGenerator] Starting AI title generation for ${request.imageUrls.length} images`);
    
    const analysis = await analyzePortfolioImages({
      imageUrls: request.imageUrls,
      userProfession: request.userProfession || 'designer',
      userSkills: [] // Don't pass user skills to focus on image analysis
    });

    console.log(`[PortfolioGenerator] AI title generation completed successfully`);
    return analysis.title;

  } catch (error) {
    console.error('[PortfolioGenerator] AI title generation failed:', error);
    return "Portfolio Project";
  }
}

/**
 * Generate only the portfolio project description using AI analysis
 */
export async function generatePortfolioDescription(
  request: PortfolioGenerationRequest
): Promise<string> {
  try {
    console.log(`[PortfolioGenerator] Starting AI description generation for ${request.imageUrls.length} images`);
    
    const analysis = await analyzePortfolioImages({
      imageUrls: request.imageUrls,
      userProfession: request.userProfession || 'designer',
      userSkills: [] // Don't pass user skills to focus on image analysis
    });

    console.log(`[PortfolioGenerator] AI description generation completed successfully`);
    // Wrap the description in proper HTML paragraphs
    const paragraphs = analysis.description.split('\n\n').filter(Boolean);
    return paragraphs.map(p => `<p>${p}</p>`).join('\n');

  } catch (error) {
    console.error('[PortfolioGenerator] AI description generation failed:', error);
    return "<p>A creative design project showcasing professional skills and expertise.</p>";
  }
} 